import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { useTanstackSheetsFetchFn } from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { ApiError } from '@/components/Functional/lib/request'
import { useStampPointLabelFromSheetTypes } from '@/components/Functional/useStampPointLabel'
import { isSpotPurchaseLocked } from '@/components/Functional/utils/calcSheetLockStatus'
import { resolveAcquireErrorKey } from '@/components/Functional/utils/stampAvailability'
import { time } from '@/components/Functional/utils/util'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { ScanCodeOverlay } from '@/components/Layout/scanner/components/ScanCodeOverlay'
import { ArPhotoOverlay } from '@/components/Models/ar-photo'
import { ActionDialog } from '@/components/Models/dialog/components/ActionDialog'
import { DialogLoginRequired } from '@/components/Models/dialog/components/DialogLoginRequired'
import { DialogUseCoupon } from '@/components/Models/dialog/components/DialogUseCoupon'
import { ShareSnsCard } from '@/components/Models/share-sns/ShareSnsCard'
import { OcrFrame } from '@/components/Pages/ocr/components'
import { OcrPreview } from '@/components/Pages/ocr/components/preview'
import {
  DialogArPhotoLocked,
  DialogAudioGuideLocked,
  DialogGetStamps,
  DialogQuizChallenge,
  DialogQuizKeyword,
  DialogQuizResult,
  DialogRedisplayStamps,
  DialogSpotsAudioGuide,
  DialogTicketRequired,
  SpotDetail,
} from '@/components/Pages/spots/components'
import { SpotDetailSkeleton } from '@/components/Pages/spots/components/detail/SpotDetailSkeleton'
import { SpotDetailStateMachineDebug } from '@/components/Pages/spots/components/detail/SpotDetailStateMachineDebug'
import { DialogCouponUsed } from '@/components/Pages/spots/components/dialogs/DialogCouponUsed'
import { DialogOcrReadFailure } from '@/components/Pages/spots/components/dialogs/DialogOcrReadFailure'
import { DialogOcrWarning } from '@/components/Pages/spots/components/dialogs/DialogOcrWarning'
import { Button } from '@/components/UI/Button'
import { Snackbar } from '@/components/UI/Snackbar'
import { extractQrHash } from '@/lib/qrUtils'
import type { SheetData } from '@/schemas/v2/sheet'
import {
  type PhotoFrameItem,
  type PhotoFrameLockReason,
  type SpotCouponEntryData,
  type SpotCouponsResponse,
  isAudioGuideLocked,
  isPhotoFrameLocked,
  pickPhotoFrameAudio,
  resolvePhotoFrameLockReason,
  toAudioGuideDialogItems,
  toPhotoFrameItems,
} from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { Loader } from '@mantine/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from '@xstate/react'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPlayableStamps } from './stampUtils'
import { useSpotDetailStateMachine } from './useSpotDetailStateMachine'

export const SpotDetailPage = ({ id }: { id: number }) => {
  const router = useRouter()
  const { gen, loginUrl, campaignSlug } = useCampaignSlug()
  const { i18n, t } = useTranslation()

  const appActorRef = AppMachineContext.useActorRef()
  const campaignData = useSelector(appActorRef, (s) => s.context.campaignData)

  const { stamp, attached } = useSpotDetailStateMachine(id, {
    isBarCodeWithGPS: campaignData?.isBarCodeWithGPS ?? false,
    isQRCodeWithGPS: campaignData?.isQRCodeWithGPS ?? false,
    isKeywordWithGPS: campaignData?.isKeywordWithGPS ?? false,
    isCampaignLoaded: campaignData != null,
  })
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  const suppressPageReload = useTokenStore((s) => s.suppressPageReload)
  const restorePageReload = useTokenStore((s) => s.restorePageReload)
  const clearShouldReloadAfterRefresh = useTokenStore((s) => s.clearShouldReloadAfterRefresh)
  const campaign = {
    type: (campaignData?.type ?? 1) as 1 | 2 | 3,
    stampShape: (campaignData?.stampShape ?? 0) as 0 | 1,
    stampPopup: undefined,
  }

  // シート種別は spot.sheetTypes から取得（スポット未取得時は campaignData.sheetTypes にフォールバック）
  const spotSheetTypes = stamp.context.spot?.sheetTypes ?? campaignData?.sheetTypes ?? []
  const { type: stampPointType, label: stampPointLabel } = useStampPointLabelFromSheetTypes(spotSheetTypes)

  const [arPhotoOpen, setArPhotoOpen] = useState(false)
  const [photoFrames, setPhotoFrames] = useState<PhotoFrameItem[]>([])
  // ARフォトのロック理由（#3445）。文言をスタンプ未獲得 / 範囲外で出し分けるために保持する
  const [arPhotoLockReason, setArPhotoLockReason] = useState<PhotoFrameLockReason | null>(null)
  // AR起動時に自動再生する音声URL（#3261）。署名付きURLのためキャッシュせず起動直前の取得結果から保持する
  const [arPhotoAudio, setArPhotoAudio] = useState<string | null>(null)
  // ARフォト・音声ガイドAPI呼び出し中の二重送信防止（描画不要なため useRef で管理）
  const arPhotoInFlightRef = useRef(false)
  const audioGuideInFlightRef = useRef(false)
  const [audioGuideItems, setAudioGuideItems] = useState<{ caption: string | null; audio: string | null }[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const [couponUseId, setCouponUseId] = useState<number | null>(null)
  const [couponRedeemLoading, setCouponRedeemLoading] = useState(false)
  const [couponRedeemError, setCouponRedeemError] = useState<string | null>(null)
  const [couponLoginRequired, setCouponLoginRequired] = useState(false)
  const [couponUsedDialogOpen, setCouponUsedDialogOpen] = useState(false)
  const [showOcrReadingGuide, setShowOcrReadingGuide] = useState(false)
  // #3414: 購入連動ロック中にスタンプ獲得が試みられた際の注意喚起ダイアログ
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false)

  // ── fromQr 自動起動 ────────────────────────────────────────────────────
  // 外部カメラで QR を読み取った場合、QR ページから fromQr パラメータが渡される
  // Next.js Pages Router は router.query を自動デコードするため decodeURIComponent は不要
  const fromQrHash = router.isReady && typeof router.query.fromQr === 'string' ? router.query.fromQr : null

  // スタンプ押印可能期間外かどうかを判定（外部カメラ自動起動前チェック用を兼ねる）
  // 1スポットに複数スタンプが紐づく場合、期間内のスタンプが1件も無いときのみ期間外とする（#2540）。
  // 各スタンプは個別の startAt/endAt を優先し、null の場合はキャンペーン期間にフォールバックする。
  // open/close による時間帯制限も考慮する（null または 00:00:00 なら全日可）。
  // ここで stamp.context.spot を直接参照するのは spot エイリアスの前方定義を避けるため。
  const isOutOfPlayingPeriod = (() => {
    const stamps = stamp.context.spot?.stamps ?? []
    if (stamps.length === 0) return false
    return (
      getPlayableStamps(
        stamps,
        { playingStartAt: campaignData?.playingStartAt, playingEndAt: campaignData?.playingEndAt },
        new Date(),
      ).length === 0
    )
  })()

  const spot = stamp.context.spot

  // 詳細レスポンスは sheetIds のみ返すため /api/v2/sheets で解決する。
  // シート名（#3365）と購入連動ロック判定（#3414）で同じマップを使い、追加リクエストを発生させない。
  const sheetsFetch = useTanstackSheetsFetchFn()
  const { data: sheetsData, isPending: isSheetsPending } = useQuery(sheetsFetch)
  const sheetById = useMemo<Map<number, SheetData>>(() => {
    const map = new Map<number, SheetData>()
    for (const sheet of sheetsData?.sheets ?? []) map.set(sheet.id, sheet)
    return map
  }, [sheetsData])

  // 購入連動ロック中（locked=true）のコースには 🔓 を付与し、スポット一覧のシート名バッジと表示を揃える（#3414）
  const courseNames = useMemo<string[]>(
    () =>
      (spot?.sheetIds ?? [])
        .map((sheetId) => sheetById.get(sheetId))
        .filter((sheet): sheet is SheetData => sheet !== undefined)
        .map((sheet) => (sheet.locked ? `🔓 ${sheet.name}` : sheet.name)),
    [sheetById, spot?.sheetIds],
  )

  // 購入連動ロック（#3414）: sheetIds が示すシートが全て locked のとき、スタンプ獲得を注意喚起に差し替える。
  // ただし獲得期間外は購入しても解消しないため、期間外の表示（非活性 +「獲得期間外です」）を優先する。
  const purchaseLocked = useMemo(
    () => !isOutOfPlayingPeriod && isSpotPurchaseLocked(spot?.sheetIds ?? [], sheetById),
    [isOutOfPlayingPeriod, spot?.sheetIds, sheetById],
  )

  // 自動起動済みフラグ（レンダー間で保持するため useRef を使用）
  const autoStampTriggeredRef = useRef(false)
  // QR_READ 送信済みフラグ（fromQr をURL除去後も再送信しないよう保持）
  const autoQrReadTriggeredRef = useRef(false)

  // ① stamp.isIdle になったら START_STAMP_FLOW を自動送信
  // 外部カメラ遷移時は期間外・購入連動ロックをチェックし、該当する場合は自動起動しない
  useEffect(() => {
    if (!fromQrHash) return
    if (autoStampTriggeredRef.current) return
    if (!stamp.isIdle) return
    // シート取得中は購入連動ロックが確定しないため待つ（#3414）
    if (isSheetsPending) return

    // 期間外・購入連動ロック中はスタンプフローを起動せず fromQr をURLから除去して終了。
    // ロック中は獲得APIを呼ばずに注意喚起ダイアログを出す（ボタン押下時と挙動を揃える）
    if (isOutOfPlayingPeriod || purchaseLocked) {
      if (purchaseLocked) setTicketDialogOpen(true)
      const { fromQr: _removed, ...restQuery } = router.query
      void router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true })
      return
    }

    autoStampTriggeredRef.current = true
    stamp.send({ type: 'START_STAMP_FLOW' })
  }, [fromQrHash, stamp.isIdle, stamp.send, isOutOfPlayingPeriod, purchaseLocked, isSheetsPending, router])

  // ② stamp.isQrScan になったら QR_READ を自動送信（カメラを開かずにハッシュを直接渡す）
  // 送信後は fromQr をURLから除去して、以降の手動スキャンを阻害しないようにする
  useEffect(() => {
    if (!fromQrHash) return
    if (autoQrReadTriggeredRef.current) return
    if (!stamp.isQrScan) return

    autoQrReadTriggeredRef.current = true
    stamp.send({ type: 'QR_READ', code: fromQrHash })
    // fromQr クエリを除去（shallow replace でページ再レンダーを最小化）
    const { fromQr: _removed, ...restQuery } = router.query
    void router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true })
  }, [fromQrHash, stamp.isQrScan, stamp.send, router])
  // ─────────────────────────────────────────────────────────────────────

  const api = useApi()
  const queryClient = useQueryClient()

  // クーポン取得（spot.coupon=true の場合のみ）
  // ログイン状態に応じて Authorization ヘッダーの付与を切り替える。
  // tokenLoading 中は enabled を false にして、ゲストリクエストが先走るのを防ぐ。
  const isAuthed = idToken !== null
  const { data: couponsData } = useQuery<SpotCouponsResponse | undefined>({
    queryKey: ['spot-coupons', id, isAuthed],
    queryFn: () =>
      spot
        ? isAuthed
          ? api.auth.fetchSpotCoupons(spot.id)
          : api.guest.fetchSpotCoupons(spot.id)
        : Promise.resolve(undefined),
    enabled: !!spot?.coupon && !tokenLoading,
  })
  const coupons: SpotCouponEntryData[] = couponsData?.coupons ?? []

  /**
   * スタンプ ID → 表示用メタ（名称・画像）。獲得ダイアログ・再表示ダイアログ・オーバーレイで使用する。
   * `GET /api/v2/spots/{id}` の `stamps[].image` から直接引くため追加リクエストは発生しない（#2540 / #3424）。
   */
  const stampViewById = useMemo(() => {
    const map: Record<number, { name: string; image: string | null }> = {}
    for (const s of spot?.stamps ?? []) {
      map[s.id] = { name: s.name, image: s.image ?? null }
    }
    return map
  }, [spot?.stamps])

  // オーバーレイに出す画像は「獲得済みのうちスタンプ ID 昇順で先頭」のもの。
  // スポット一覧・マップのピン画像（buildStampImageBySpotId）と同じ基準にそろえている。
  const stampImageUrl = useMemo(() => {
    // 必要なのは最小 ID のみなので、コピー + ソートせず1パスで求める
    const firstAcquiredId = stamp.context.acquiredStampIds.reduce<number | null>(
      (min, id) => (min === null || id < min ? id : min),
      null,
    )
    if (firstAcquiredId !== null) return stampViewById[firstAcquiredId]?.image ?? undefined
    // 未獲得時は対象スタンプの画像（status='display' 用のフォールバック）
    return stamp.context.activeStampId != null
      ? (stampViewById[stamp.context.activeStampId]?.image ?? undefined)
      : undefined
  }, [stamp.context.acquiredStampIds, stamp.context.activeStampId, stampViewById])
  const nonStampImageUrl = campaignData?.nonStampImage ?? null

  // tokenStore のロードが完了したら認証状態をマシンに送信（waitingForAuth での待機用）
  useEffect(() => {
    if (tokenLoading) return
    stamp.send({ type: 'AUTH_READY', isAuthenticated: idToken !== null })
  }, [tokenLoading, idToken, stamp.send])

  // GPS範囲外エラーSnackbarを4秒後に自動消去
  useEffect(() => {
    if (!stamp.isGpsRangeError) return
    const timer = setTimeout(() => stamp.send({ type: 'GPS_RANGE_ERROR_CLOSE' }), 4000)
    return () => clearTimeout(timer)
  }, [stamp.isGpsRangeError, stamp.send])

  // スタンプ獲得完了時に関連キャッシュを無効化
  useEffect(() => {
    if (!stamp.isStampShowing) return
    // スポット一覧マップのピン画像を即時更新
    void queryClient.invalidateQueries({ queryKey: ['userStamps', campaignSlug] })
    // スタンプ連動クーポンのステータスを即時更新
    void queryClient.invalidateQueries({ queryKey: ['spot-coupons', id, isAuthed] })
  }, [stamp.isStampShowing, campaignSlug, id, isAuthed, queryClient])

  // 401 エラー時: tokenRefresh が waiting になったら REFRESH_COMPLETE を送信（race condition 対応）
  const isTokenRefreshWaiting = AppMachineContext.useSelector((s) => s.matches({ phase3: { tokenRefresh: 'waiting' } }))
  const stampRefreshRef = useRef({
    isTokenRefreshWaiting,
    isWaitingForRefresh: stamp.state.matches('waitingForRefresh'),
  })
  useEffect(() => {
    const prev = stampRefreshRef.current
    const isWaitingForRefresh = stamp.state.matches('waitingForRefresh')
    const current = { isTokenRefreshWaiting, isWaitingForRefresh }
    stampRefreshRef.current = current

    const tokenJustEnteredWaiting = current.isTokenRefreshWaiting && !prev.isTokenRefreshWaiting
    const stateJustEnteredWaiting = current.isWaitingForRefresh && !prev.isWaitingForRefresh

    if (
      current.isWaitingForRefresh &&
      current.isTokenRefreshWaiting &&
      (tokenJustEnteredWaiting || stateJustEnteredWaiting)
    ) {
      stamp.send({ type: 'REFRESH_COMPLETE' })
    }
  }, [isTokenRefreshWaiting, stamp.state, stamp.send])

  // OCR 読み取り失敗: OCR_FAILURE_APPLY で ocrForm へ遷移
  const handleOcrFailureApply = () => {
    const ocrFailureId = stamp.ocrFailureId
    if (ocrFailureId === null) return
    void router.push(gen(`/ocrForm?ocrId=${ocrFailureId}`))
    stamp.send({ type: 'OCR_FAILURE_APPLY' })
  }

  // OCR 注意喚起データ（API から取得）
  const ocrWarning = spot?.stamps.find((s) => s.id === stamp.context.activeStampId)?.ocrWarning ?? null

  // ARカメラ使用中はページリロードを抑止する。
  // arPhotoOpen（手動ARフォト）または stamp.isPhotoPost（スタンプ取得後ARフォト）が
  // true の場合に suppressPageReload() を呼び、ARカメラ終了時に restorePageReload() と
  // clearShouldReloadAfterRefresh() を呼んでフラグを解除する。
  // これにより、ARカメラ中に 401 → token/refresh が発生しても
  // _app.tsx の window.location.reload() がスキップされる。
  const isArCameraActive = arPhotoOpen || stamp.isPhotoPost
  useEffect(() => {
    if (!isArCameraActive) return
    suppressPageReload()
    return () => {
      restorePageReload()
      // ARカメラ使用中に 401 が発生していた場合、リロードフラグを解除する。
      clearShouldReloadAfterRefresh()
    }
  }, [isArCameraActive, suppressPageReload, restorePageReload, clearShouldReloadAfterRefresh])

  // suppressPageReload() 等による親再レンダリング時に、memo された ArPhotoOverlay が
  // 不要に再レンダーされないよう参照を安定化する
  // （履歴管理は ArPhotoOverlay 側が onCloseRef で onClose の参照変化に依存しない構造になっている）。
  const handleArPhotoClose = useCallback(() => setArPhotoOpen(false), [])
  // フォト投稿オーバーレイ用も同様に参照を安定化する
  const handlePhotoPostClose = useCallback(() => stamp.send({ type: 'PHOTO_CLOSE' }), [stamp.send])

  if (stamp.isLoading) {
    return (
      // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
      // 旧実装は height: calc(100dvh - 58px) でヘッダー高さ（44px）を直書きしていた。
      // pb-[72px] は fixed 配置の BottomNavigation（72px）に最終行が隠れないための余白。
      // 旧実装の pb-[60px] は main が 14px 短かった分で帳尻を合わせていたため、他ページと同じ 72px に揃える。
      <div className="flex h-dvh flex-col">
        <Header
          title={t('spot_detail.header_title', 'スポット詳細')}
          showBack
          onBack={() => router.push(gen('/spots'))}
          showNotification={false}
        />
        <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]">
          <SpotDetailSkeleton />
        </main>
        <BottomNavigation activeItem="spot" />
      </div>
    )
  }

  if (stamp.isError) {
    return (
      <div className="flex h-dvh flex-col">
        <Header
          title={t('spot_detail.header_title', 'スポット詳細')}
          showBack
          onBack={() => router.push(gen('/spots'))}
          showNotification={false}
        />
        <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]">
          <div className="flex flex-col items-center justify-center h-full gap-4 p-6 text-center">
            <p className="text-sm text-gray-600">{t('spot_detail.fetch_failed', 'スポット情報の取得に失敗しました')}</p>
            <button
              type="button"
              className="px-4 py-2 bg-main text-white rounded-full text-sm"
              onClick={() => stamp.send({ type: 'RESET' })}
            >
              {t('common.retry', '再試行')}
            </button>
          </div>
        </main>
        <BottomNavigation activeItem="spot" />
      </div>
    )
  }

  // スタンプ表示用データ（複数スタンプを送り送りするため currentStampIndex に連動させる・#2540）
  // ── 獲得直後ダイアログ: acquiredStamps（POST /api/v2/stamps/{id} のレスポンス）を辿る。
  // レスポンスの name / image / point（#3424）を優先し、旧レスポンス互換で spot.stamps 由来にフォールバックする。
  const isLastStamp = stamp.context.currentStampIndex >= stamp.context.acquiredStamps.length - 1
  const acquiredStampEntry = stamp.context.acquiredStamps[stamp.context.currentStampIndex]
  const acquiredStampView = acquiredStampEntry?.stampId != null ? stampViewById[acquiredStampEntry.stampId] : undefined
  // スタンプ画像が未登録なら何も表示しない。スポット画像・他スタンプ画像へフォールバックしない（#3450）。
  // acquiredStampEntry.image（POST レスポンス）と acquiredStampView.image（GET /spots の stamps[]）は
  // どちらも「獲得したスタンプ自身」の画像なので、旧レスポンス互換のためこの 2 段のみ残す。
  const stampDisplayImage = acquiredStampEntry?.image ?? acquiredStampView?.image ?? null
  /** 獲得したポイント数（ポイントラリー系キャンペーンのみダイアログに表示される） */
  const acquiredStampPoint = acquiredStampEntry?.point ?? null
  /** 獲得したスタンプ名。複数スタンプのスポットでどれを獲得したか判別できるよう表示する（再表示ダイアログと同じ） */
  const acquiredStampName = acquiredStampEntry?.name ?? acquiredStampView?.name ?? spot?.name ?? ''

  // ── 再表示ダイアログ: 獲得済みスタンプ（redisplayStampIds）を辿る
  const isLastRedisplayStamp = stamp.context.currentStampIndex >= stamp.context.redisplayStampIds.length - 1
  const redisplayStampId = stamp.context.redisplayStampIds[stamp.context.currentStampIndex] ?? null
  const redisplayStampView = redisplayStampId != null ? stampViewById[redisplayStampId] : undefined
  // 獲得ダイアログと同様、対象スタンプ自身の画像のみを使う（#3450）
  const redisplayStampImage = redisplayStampView?.image ?? null
  const redisplayStampName = redisplayStampView?.name ?? spot?.name ?? ''

  const hasArPhoto = spot?.photoFrame ?? false

  /**
   * 位置情報を取得できていないか（#3445）。
   * AR フォト・音声ガイドはサーバー側で距離判定しており、座標が無い場合も一律 out_of_range が返る
   * （GetSpotPhotoFrameUseCase / GetSpotAudioGuideUseCase）。
   * そのため「範囲外」ではなく「位置情報が取得できていない」ことを案内する必要がある。
   */
  const isLocationUnavailable = stamp.gpsLoaded && !stamp.context.gps.available

  // officialSns をそのまま SpotDetail へ渡す（配列の順で表示される）
  const socialLinks = spot?.officialSns

  // ARフォトボタン押下時: GPS 付きで POST して結果に応じてフレームをセットまたはロックダイアログを表示
  // stampLinked=true のスポットはバックエンドが認証ユーザーのスタンプ取得状況を確認するため、
  // ログイン済みの場合は Authorization ヘッダー付き (api.auth) で呼び出す。
  const handleArPhotoClick = async () => {
    if (arPhotoInFlightRef.current) return
    arPhotoInFlightRef.current = true
    try {
      const gps = stamp.context.gps
      // 取得できていない位置情報は送らない（#3445）。(0,0) を送るとサーバーが有効な座標として距離判定してしまう
      const body = gps.available ? { lat: gps.lat, lon: gps.lon } : {}
      const res =
        idToken !== null ? await api.auth.postSpotPhotoFrames(id, body) : await api.guest.postSpotPhotoFrames(id, body)
      if (isPhotoFrameLocked(res.photoFrames)) {
        setArPhotoLockReason(resolvePhotoFrameLockReason(res.photoFrames))
        attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
      } else {
        setPhotoFrames(toPhotoFrameItems(res.photoFrames))
        setArPhotoAudio(pickPhotoFrameAudio(res.photoFrames))
        setArPhotoOpen(true)
      }
    } catch {
      // 理由を特定できないため既定（スタンプ未獲得）の文言にフォールバックする
      setArPhotoLockReason(null)
      attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
    } finally {
      arPhotoInFlightRef.current = false
    }
  }

  // 音声ガイドボタン押下時: GPS 付きで POST して結果に応じてダイアログを表示またはロックダイアログを表示
  const handleAudioGuideClick = async () => {
    if (audioGuideInFlightRef.current) return
    audioGuideInFlightRef.current = true
    try {
      const gps = stamp.context.gps
      // 取得できていない位置情報は送らない（#3445）。(0,0) を送るとサーバーが有効な座標として距離判定してしまう
      const body = gps.available ? { lat: gps.lat, lon: gps.lon } : {}
      const res = await api.guest.postSpotAudioGuides(id, body)
      if (!res) {
        attached.send({ type: 'OPEN_AUDIO_GUIDE_LOCKED' })
        return
      }
      const entries = res.audioGuides
      if (isAudioGuideLocked(entries)) {
        attached.send({ type: 'OPEN_AUDIO_GUIDE_LOCKED' })
      } else {
        setAudioGuideItems(toAudioGuideDialogItems(entries))
        attached.send({ type: 'OPEN_AUDIO_GUIDE' })
      }
    } catch {
      attached.send({ type: 'OPEN_AUDIO_GUIDE_LOCKED' })
    } finally {
      audioGuideInFlightRef.current = false
    }
  }

  // クイズの現在の問題（context.quizzes は初期ロード時に取得済み）
  const currentQuiz = stamp.context.quizzes[0]

  // クーポン使用確定時: API を呼び出してクーポンを消費する
  const handleCouponUse = async () => {
    if (couponUseId === null) return
    if (idToken === null) {
      setCouponLoginRequired(true)
      setCouponUseId(null)
      return
    }
    setCouponRedeemLoading(true)
    try {
      await api.auth.postSpotCouponRedeem(id, couponUseId)
      setCouponUseId(null)
      void queryClient.invalidateQueries({ queryKey: ['spot-coupons', id, true] })
      setCouponUsedDialogOpen(true)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          // ボタン表示（使用済み / 本日は利用済み / 利用上限）をサーバ実態に追従させる
          void queryClient.invalidateQueries({ queryKey: ['spot-coupons', id, isAuthed] })
          setCouponRedeemError(
            t('coupon.redeem_error.used_or_limit', 'このクーポンはすでに使用済みか、利用上限に達しています。'),
          )
        } else if (e.status === 403) {
          setCouponLoginRequired(true)
          setCouponUseId(null)
        } else {
          setCouponRedeemError(
            t('coupon.redeem_error.failed', 'クーポンの利用に失敗しました。しばらくしてから再度お試しください。'),
          )
        }
      } else {
        setCouponRedeemError(
          t('coupon.redeem_error.failed', 'クーポンの利用に失敗しました。しばらくしてから再度お試しください。'),
        )
      }
    } finally {
      setCouponRedeemLoading(false)
    }
  }

  return (
    <>
      {/* ヘッダーと main のみを h-dvh の flex コンテナに入れ、main をスクロールコンテナにする。
          ダイアログ・オーバーレイ類は fixed / portal 描画なので、
          不要に flex アイテム化しないようコンテナの外に置く。 */}
      <div className="flex h-dvh flex-col">
        {!stamp.isOcrCapture && !stamp.isOcrPreview && (
          <Header
            title={t('spot_detail.header_title', 'スポット詳細')}
            showBack
            onBack={() => router.push(gen('/spots'))}
            showNotification={false}
          />
        )}
        {stamp.isOcrCapture ? (
          <>
            <OcrFrame
              onCaptured={(imageDataUrl) => {
                setShowOcrReadingGuide(false)
                stamp.send({ type: 'OCR_CAPTURED', image: imageDataUrl })
              }}
              onGoToWarning={() => setShowOcrReadingGuide(true)}
              onClose={() => {
                setShowOcrReadingGuide(false)
                stamp.send({ type: 'OCR_CAPTURE_CLOSE' })
              }}
            />
            <DialogOcrWarning
              opened={showOcrReadingGuide}
              ocrWarning={ocrWarning}
              onClose={() => setShowOcrReadingGuide(false)}
            />
          </>
        ) : stamp.isOcrPreview && stamp.ocrCapturedImage ? (
          <OcrPreview
            imageDataUrl={stamp.ocrCapturedImage}
            onSubmit={() => stamp.send({ type: 'OCR_PREVIEW_SUBMIT' })}
            onRetake={() => stamp.send({ type: 'OCR_PREVIEW_RETAKE' })}
            onClose={() => stamp.send({ type: 'OCR_PREVIEW_CLOSE' })}
          />
        ) : (
          <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]">
            {spot ? (
              <SpotDetail
                name={spot.name}
                imageUrls={spot.images}
                address={spot.address ?? undefined}
                url={spot.url ?? undefined}
                description={spot.description ?? undefined}
                spotLocation={
                  spot.latitude != null && spot.longitude != null
                    ? { latitude: spot.latitude, longitude: spot.longitude }
                    : undefined
                }
                courseNames={courseNames}
                canGetStamp={(spot.stamps?.length ?? 0) > 0}
                stampAcquired={stamp.context.hasAllStamps}
                anyStampAcquired={stamp.context.hasAnyStamp}
                stampUnavailableReason={stamp.context.stampUnavailableReason}
                stampOutOfPeriod={isOutOfPlayingPeriod}
                stampImageUrl={stampImageUrl}
                nonStampImageUrl={nonStampImageUrl}
                stampShape={campaign.stampShape}
                stampPointType={stampPointType}
                // 1件でも獲得済みなら再表示導線を出す（#2540）
                onStampOverlayClick={
                  stamp.context.hasAnyStamp ? () => stamp.send({ type: 'STAMP_REDISPLAY' }) : undefined
                }
                purchaseLocked={purchaseLocked}
                // シート取得中はロック判定が確定しないため、獲得ボタンを押させない（#3414）。
                // 一方 /api/v2/sheets がエラー確定した場合（isPending=false かつ data 無し）は
                // 意図的にフェイルオープンし、ボタンを活性のままにする。
                // シート情報が引けないだけで全スポットの獲得を止めると、障害時に現地のユーザーが
                // スタンプを取得できなくなるため。ロック中スポットはサーバー側の 403（SHEET_LOCKED）が最終ゲートになる。
                stampCheckLoading={isSheetsPending}
                onStampGet={() => {
                  // 購入連動ロック中は獲得APIを呼ばず、購入が必要な旨の注意喚起のみ行う（購入導線はホームに集約）
                  if (purchaseLocked) {
                    setTicketDialogOpen(true)
                    return
                  }
                  stamp.send({ type: 'START_STAMP_FLOW' })
                }}
                stampOutOfRange={spot.isInRange === false}
                // GPS 種別に限らず、キャンペーンで GPS 併用が有効な QR / バーコード / キーワードでも
                // 位置情報が無いと獲得できない（サーバーが STAMP_LOCATION_REQUIRED を返す）ため、
                // 同じように許可を促す（#3445）
                stampGpsPermissionDenied={
                  stamp.context.gps.loaded && stamp.context.gps.permissionDenied && stamp.stampRequiresGps
                }
                hasAudioGuide={spot.audioGuide}
                onAudioGuideClick={handleAudioGuideClick}
                audioGuideLoading={!stamp.gpsLoaded || stamp.isGpsRefreshing}
                geoLoading={!stamp.gpsLoaded || stamp.isGpsRefreshing || tokenLoading}
                hasArPhoto={hasArPhoto}
                arPhotoLocked={stamp.context.photoFrameInitiallyStampRequired && !stamp.context.hasAnyStamp}
                onArPhotoClick={handleArPhotoClick}
                coupons={coupons}
                isCoupons={spot.coupon}
                tags={spot.tags}
                socialLinks={socialLinks}
                onCouponUse={(couponId) => {
                  if (idToken === null) {
                    setCouponLoginRequired(true)
                  } else {
                    setCouponUseId(couponId)
                  }
                }}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            ) : (
              <p className="text-center py-8 text-gray-500">
                {t('spot_detail.not_found', 'スポットが見つかりませんでした')}
              </p>
            )}
          </main>
        )}
      </div>
      {!stamp.isOcrCapture && !stamp.isOcrPreview && !stamp.isOcrWarning && <BottomNavigation activeItem="spot" />}

      <SpotDetailStateMachineDebug stamp={stamp} attached={attached} />

      {/* ── スタンプ獲得フロー ── */}

      {(() => {
        const isDescriptiveType = currentQuiz?.quizType === 1
        return (
          <>
            {(stamp.isDescriptiveQuizInput || (stamp.isQuizAnswering && isDescriptiveType)) && (
              <DialogQuizKeyword
                scrollLock
                onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
                title={currentQuiz?.title}
                question={currentQuiz?.question ?? t('spot_detail.input_keyword', 'キーワードを入力してください')}
                image={currentQuiz?.questionImage ?? null}
                onSubmit={(answer) => stamp.send({ type: 'TEXT_ANSWER_SUBMIT', answer })}
                isLoading={stamp.isQuizAnswering}
              />
            )}

            {(stamp.isQuizChoice || (stamp.isQuizAnswering && !isDescriptiveType)) && (
              <DialogQuizChallenge
                scrollLock
                onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
                title={currentQuiz?.title}
                question={currentQuiz?.question ?? t('spot_detail.quiz.answer_prompt', 'クイズに答えてください')}
                image={currentQuiz?.questionImage ?? null}
                choices={currentQuiz?.choices ?? []}
                onSelect={(choiceId) => stamp.send({ type: 'ANSWER_SUBMIT', choiceId })}
                isLoading={stamp.isQuizAnswering}
              />
            )}
          </>
        )
      })()}

      {stamp.isQuizCorrect && (
        <DialogQuizResult
          scrollLock
          result="correct"
          heading={stamp.quizAnswerResult?.answer ?? t('spot_detail.quiz.correct_heading', '正解！')}
          message={stamp.quizAnswerResult?.message ?? undefined}
          image={stamp.quizAnswerResult?.correctImage ?? undefined}
          onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
          onAction={() => stamp.send({ type: 'STAMP_GET' })}
          actionLabel={
            stampPointType === 'POINT'
              ? t('spot_detail.get_a_point', 'ポイントを獲得する')
              : stampPointType === 'STAMP_POINT'
                ? t('spot_detail.get_stamp_or_point', '獲得する')
                : t('spot_detail.get_a_stamp', 'スタンプを獲得する')
          }
        />
      )}

      {stamp.isQuizIncorrect && (
        <DialogQuizResult
          scrollLock
          result="incorrect"
          heading={
            stamp.canRetryQuiz
              ? t('spot_detail.quiz.incorrect', '不正解...')
              : t('spot_detail.quiz.daily_limit_reached', '本日の回答権を使い切りました')
          }
          message={stamp.canRetryQuiz ? (stamp.quizAnswerResult?.hintMessage ?? undefined) : undefined}
          image={stamp.quizAnswerResult?.incorrectImage ?? undefined}
          onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
          onAction={
            stamp.canRetryQuiz ? () => stamp.send({ type: 'QUIZ_RETRY' }) : () => stamp.send({ type: 'QUIZ_CLOSE' })
          }
          actionLabel={
            stamp.canRetryQuiz ? t('spot_detail.quiz.retry', 'もう一度') : t('spot_detail.quiz.close', 'とじる')
          }
        />
      )}

      {/* 日次制限エラーダイアログ */}
      <ActionDialog
        selectorState={stamp.isQuizDailyLimitError}
        variant="message"
        title={t('spot_detail.quiz.title', 'クイズ')}
        description={t(
          'spot_detail.quiz.daily_limit_exhausted_message',
          '本日のクイズ回答は終了しました。<br>また明日チャレンジしてください。',
        )}
        cancelLabel={t('spot_detail.quiz.daily_limit_close', 'とじる')}
        closeAction={() => stamp.send({ type: 'QUIZ_DAILY_LIMIT_CLOSE' })}
      />

      {/* QR コード読み取り */}
      {/* fromQrHash がある場合はカメラオーバーレイを開かず、useEffect で直接 QR_READ を送信する */}
      <ScanCodeOverlay
        opened={stamp.isQrScan && !fromQrHash}
        mode="qr"
        onClose={() => stamp.send({ type: 'QR_CLOSE' })}
        onRead={async (result) => {
          const hash = extractQrHash(result.getText())
          if (hash === null) {
            stamp.send({ type: 'QR_WRONG_FORMAT' })
            return true
          }
          stamp.send({ type: 'QR_READ', code: hash })
          return true
        }}
      />

      {/* バーコード読み取り */}
      <ScanCodeOverlay
        opened={stamp.isBarcodeScan}
        mode="barcode"
        onClose={() => stamp.send({ type: 'BARCODE_CLOSE' })}
        onRead={async (result) => {
          const code = result.getText()
          if (!code) return false
          stamp.send({ type: 'BARCODE_READ', code })
          return true
        }}
      />

      {/* OCR 注意喚起ダイアログ */}
      <DialogOcrWarning
        opened={stamp.isOcrWarning}
        ocrWarning={ocrWarning}
        onConfirm={() => stamp.send({ type: 'OCR_WARNING_CONFIRM' })}
        onClose={() => stamp.send({ type: 'OCR_WARNING_CLOSE' })}
      />

      {/* OCR 読み取り失敗ダイアログ */}
      {stamp.isOcrReadFailure && (
        <DialogOcrReadFailure
          scrollLock
          variant={
            stamp.isOcrRetryable
              ? 'retryable'
              : stamp.isOcrCheckingApplication
                ? 'checking'
                : stamp.isOcrApplicable
                  ? 'applicable'
                  : 'notApplicable'
          }
          onRetry={() => stamp.send({ type: 'OCR_FAILURE_RETRY' })}
          onApply={handleOcrFailureApply}
          onClose={() => stamp.send({ type: 'OCR_FAILURE_CLOSE' })}
        />
      )}

      {/* 位置情報の取得中・API呼び出し中ローディング。
          測位は精度ゲートの打ち切り（GPS_ACCURACY_GATE_TIMEOUT）まで最大10秒かかるため、
          gpsRefreshing も含めないとボタン押下から無反応の区間ができる（#3445） */}
      {(stamp.isGpsRefreshing || stamp.isAcquiring) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
            <Loader size="lg" color="var(--main-color)" />
            <p className="text-sm text-gray-600">
              {stamp.isGpsRefreshing
                ? t('spot_detail.acquiring_location', '位置情報を取得中...')
                : stampPointType === 'POINT'
                  ? t('spot_detail.acquiring_point', 'ポイントを獲得中...')
                  : t('spot_detail.acquiring_stamp', 'スタンプを獲得中...')}
            </p>
          </div>
        </div>
      )}

      {/* スタンプ獲得完了ダイアログ */}
      <DialogGetStamps
        opened={stamp.isStampShowing}
        campaign={campaign}
        image={stampDisplayImage}
        point={acquiredStampPoint}
        stampName={acquiredStampName}
        title={
          stampPointType === 'POINT'
            ? t('spot_detail.point_acquire.you_have_acquired', 'ポイントを獲得しました！')
            : t('spot_detail.stamp_acquire.you_have_acquired', 'スタンプを獲得しました！')
        }
        onClose={isLastStamp ? () => stamp.send({ type: 'STAMP_NEXT' }) : undefined}
        closeButtonOff={!isLastStamp}
      >
        {/* 拡散用SNS（#3285）: SNS拡散が有効なスタンプ獲得時のみ表示 */}
        {stamp.context.shareSns?.enabled && spot && (
          <ShareSnsCard
            shareSns={stamp.context.shareSns}
            spotId={spot.id}
            context="stamp"
            stampId={stamp.context.acquiredStamps[stamp.context.currentStampIndex]?.stampId ?? null}
          />
        )}

        <div className="mt-6 flex justify-center">
          <Button
            label={isLastStamp ? t('common.close', '閉じる') : t('common.next', '次へ')}
            variant="primary"
            size="md"
            onClick={() => stamp.send({ type: 'STAMP_NEXT' })}
            fullWidth
          />
        </div>
      </DialogGetStamps>

      {/* 特典獲得 Snackbar + ARフォト利用可能通知 Snackbar（縦並び・✕ボタン押下まで表示） */}
      {(stamp.isRewardShowing || stamp.isArPhotoUnlockedShowing) && (
        <div className="fixed bottom-20 left-0 right-0 z-50 px-4 flex flex-col gap-2">
          {stamp.isRewardShowing && stamp.context.acquiredRewards.length > 0 && (
            <Snackbar
              message={t('spot_detail.reward_snackbar.message', {
                count: stamp.context.acquiredRewards.length,
              })}
              actionLabel={t('spot_detail.reward_snackbar.action', '確認する')}
              onClose={() => stamp.send({ type: 'REWARD_CLOSE' })}
              onAction={() => {
                stamp.send({ type: 'REWARD_CLOSE' })
                void router.push(gen('/rewards'))
              }}
            />
          )}
          {stamp.isArPhotoUnlockedShowing && (
            <Snackbar
              message={t('spot_detail.ar_photo_unlocked.message')}
              onClose={() => stamp.send({ type: 'AR_PHOTO_UNLOCKED_CLOSE' })}
            />
          )}
        </div>
      )}

      {/* QRコード不正エラーダイアログ */}
      <ActionDialog
        selectorState={stamp.isQrWrongFormat}
        variant="message"
        description={t('spot_detail.qr_wrong_format.message', '正しいQRコードを読み込んでください。')}
        cancelLabel={t('common.close', '閉じる')}
        closeAction={() => {
          stamp.send({ type: 'QR_WRONG_FORMAT_CLOSE' })
        }}
      />

      {/* スタンプ獲得APIエラーダイアログ */}
      <ActionDialog
        selectorState={stamp.isAcquireError}
        variant="message"
        description={t(resolveAcquireErrorKey(stamp.context.errorMessage, stampPointType), { what: stampPointLabel })}
        cancelLabel={t('common.close', '閉じる')}
        closeAction={() => {
          stamp.send({ type: 'ACQUIRE_ERROR_CLOSE' })
        }}
      />

      {/* 測位精度が不足していて獲得APIを呼べなかった場合のダイアログ（#3445）。
          粗い座標で距離判定すると誤判定になるため、スポットへ近づくよう促す */}
      <ActionDialog
        selectorState={stamp.isGpsAccuracyError}
        variant="message"
        description={t('spot_detail.close_to_the_spot', 'スポットに近づいてください')}
        cancelLabel={t('common.close', '閉じる')}
        closeAction={() => {
          stamp.send({ type: 'GPS_ACCURACY_ERROR_CLOSE' })
        }}
      />

      {/* GPS範囲外エラー Snackbar（画面下部・4秒後自動消去） */}
      {stamp.isGpsRangeError && (
        <div className="fixed bottom-20 left-0 right-0 z-50 px-4">
          <Snackbar
            message={
              stampPointType === 'POINT'
                ? t(
                    'spot_detail.gps_range_error.point',
                    'ポイント獲得可能な範囲内にいません。スポットに近づいてから再度お試しください。',
                  )
                : t(
                    'spot_detail.gps_range_error.stamp',
                    'スタンプ獲得可能な範囲内にいません。スポットに近づいてから再度お試しください。',
                  )
            }
            onClose={() => stamp.send({ type: 'GPS_RANGE_ERROR_CLOSE' })}
            actionLabel={t('common.retry', '再試行')}
            onAction={() => stamp.send({ type: 'GPS_RANGE_ERROR_RETRY' })}
          />
        </div>
      )}

      {/* フォトアップロード（ガイド音声は手動ARフロー専用のため audioUrl は渡さない） */}
      {stamp.isPhotoPost && (
        <ArPhotoOverlay open photoFrames={photoFrames} onClose={handlePhotoPostClose} snsHashtag="" spotId={spot?.id} />
      )}

      {/* 再表示: スタンプダイアログ */}
      <DialogRedisplayStamps
        opened={stamp.isRedisplayStampShowing}
        campaign={campaign}
        image={redisplayStampImage}
        stampName={redisplayStampName}
        isLastStamp={isLastRedisplayStamp}
        title={
          // シート種別（stampPointType）に応じて名詞を切り替える。獲得ボタン等と同じ3分岐に揃える
          stampPointType === 'POINT'
            ? t('spot_detail.show_acquired_point_again', '獲得済みポイント')
            : stampPointType === 'STAMP_POINT'
              ? t('spot_detail.show_acquired_stamp_or_point_again', '獲得済み')
              : t('spot_detail.show_acquired_stamp_again', '獲得済みスタンプ')
        }
        onClose={() => stamp.send({ type: 'STAMP_REDISPLAY_STAMP_NEXT' })}
        onAllClose={() => stamp.send({ type: 'STAMP_REDISPLAY_STAMP_NEXT' })}
      />

      {/* ── 付帯ダイアログ（音声ガイド・ARフォト） ── */}

      {attached.isArPhotoLocked && (
        <DialogArPhotoLocked
          scrollLock
          stampLabel={stampPointLabel}
          reason={arPhotoLockReason}
          locationUnavailable={isLocationUnavailable}
          onClose={() => attached.send({ type: 'CLOSE' })}
        />
      )}

      {stamp.isNotAuthenticatedError && (
        <DialogLoginRequired
          opened
          scrollLock
          loginUrl={loginUrl}
          onClose={() => stamp.send({ type: 'NOT_AUTH_ERROR_CLOSE' })}
        />
      )}
      {attached.isAudioGuide && (
        <DialogSpotsAudioGuide
          scrollLock
          onClose={() => {
            attached.send({ type: 'CLOSE' })
            setAudioGuideItems([])
          }}
          audioGuides={audioGuideItems}
        />
      )}
      {attached.isAudioGuideLocked && (
        <DialogAudioGuideLocked
          scrollLock
          locationUnavailable={isLocationUnavailable}
          onClose={() => attached.send({ type: 'CLOSE' })}
        />
      )}

      {(() => {
        const selectedCoupon = coupons.find((c) => c.id === couponUseId)
        return (
          <DialogUseCoupon
            opened={couponUseId !== null}
            onClose={() => {
              if (!couponRedeemLoading) setCouponUseId(null)
            }}
            onUseCoupon={handleCouponUse}
            couponName={selectedCoupon?.name ?? ''}
            couponImage={selectedCoupon?.image}
            usagePeriodFrom={
              selectedCoupon?.startAt
                ? (time({ time: selectedCoupon.startAt, language: i18n.language }) ?? undefined)
                : undefined
            }
            usagePeriodTo={
              selectedCoupon?.endAt
                ? (time({ time: selectedCoupon.endAt, language: i18n.language }) ?? undefined)
                : undefined
            }
            title={t('mynavi.dialog.staff_confirmation_title', 'スタッフ確認用画面')}
            warningLine1={t('reward_detail.coupon.before_press_button_01', 'クーポンを使用するときに必ずスタッフに')}
            warningLine2={t('reward_detail.coupon.before_press_button_02', 'この画面を見せてください。')}
            swipeText={t('mynavi.dialog.swipe_to_use_coupon', '右にスワイプで使用')}
            isLoading={couponRedeemLoading}
          />
        )
      })()}

      {couponLoginRequired && (
        <DialogLoginRequired opened scrollLock loginUrl={loginUrl} onClose={() => setCouponLoginRequired(false)} />
      )}

      {/* 購入連動ロック中のスタンプ獲得ボタン押下時の注意喚起（#3414） */}
      <DialogTicketRequired
        opened={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        message={t(
          'spot_detail.ticket_required.message',
          'スタンプを獲得するにはチケット購入またはコース購入が必要になります',
        )}
      />

      {/* クーポン使用完了ダイアログ */}
      <DialogCouponUsed opened={couponUsedDialogOpen} onClose={() => setCouponUsedDialogOpen(false)} />

      {couponRedeemError !== null && (
        <div className="fixed bottom-20 left-0 right-0 z-50 px-4">
          <Snackbar message={couponRedeemError} onClose={() => setCouponRedeemError(null)} />
        </div>
      )}

      {/* 手動ARフォト（スタンプ取得後フローとは独立） */}
      <ArPhotoOverlay
        open={arPhotoOpen}
        photoFrames={photoFrames}
        audioUrl={arPhotoAudio}
        onClose={handleArPhotoClose}
        onSaveSuccess={handleArPhotoClose}
        snsHashtag=""
        spotId={spot?.id}
      />
    </>
  )
}
